import { useEffect, useState } from 'react'
import { FormProvider, useForm, type FieldErrors, type Resolver } from 'react-hook-form'
import { useMediaQuery } from '@mui/material'
import { parseISO } from 'date-fns'

import { adjustTotalPeopleModalSchema } from './form/validations'
import { AdjustHistory } from './components/AdjustHistory'
import { BaseModal2 } from '@components/modals/BaseModal2'
import { Button } from '@components/shared/Button'
import { DatePicker } from '@components/shared/DatePicker'
import { emotionTheme } from '@styles/theme'
import { initialFormValues } from './form/initialFormValues'
import { Input } from '@components/shared/Input'
import { PlusCircleIcon } from '@assets/svgs/PlusCircleIcon'
import { ReturnModal } from '@components/modals/ReturnModal'
import { RowBox } from '@components/shared/RowBox'
import { Select } from '@components/shared/Select'
import { Textarea } from '@components/shared/Textarea'
import { useUser } from '@hooks/useUser'
import { WarningIcon } from '@assets/svgs/WarningIcon'

import type { AdjustTotalPeopleModalProps } from './types'
import type { AdjustTotalPeopleModalFormProps } from './form/types'

import * as S from './styles'

const LAUNCH_OPTIONS = [{ value: 'Ajuste Geral de Prévia', label: 'Ajuste Geral de Prévia' }]

export const AdjustTotalPeopleModal = ({
  open,
  onClose,
  onSubmitForm,
  isLoading,
  canEdit = true,
  ...rest
}: AdjustTotalPeopleModalProps) => {
  const isMobile = useMediaQuery(`(max-width: ${emotionTheme.screenSizes.laptop})`)
  const { filiData } = useUser()

  const [openCancelModal, setOpenCancelModal] = useState(false)
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0)

  const methods = useForm<AdjustTotalPeopleModalFormProps>({
    defaultValues: initialFormValues,
    resolver: adjustTotalPeopleModalSchema as unknown as Resolver<AdjustTotalPeopleModalFormProps>
  })

  const onError = (errors: FieldErrors<AdjustTotalPeopleModalFormProps>) => console.log({ errors })

  const onSubmit = async (formData: AdjustTotalPeopleModalFormProps) => {
    if (!canEdit) return

    formData.filiCode = filiData?.filiCode
    formData.competenceDate = filiData?.startPeriod

    // CA07: modal segue aberto; novo ajuste aparece no histórico
    if (await onSubmitForm(formData)) {
      methods.reset(initialFormValues)
      setHistoryRefreshKey((key) => key + 1)
    }
  }

  // Só pede confirmação se houver algo preenchido que seria perdido
  const handleClose = () => (methods.formState.isDirty ? setOpenCancelModal(true) : onClose(false))

  useEffect(() => {
    if (!open) {
      methods.reset(initialFormValues)
    }
  }, [open])

  return (
    <>
      <BaseModal2
        open={open}
        width={isMobile ? 'calc(100% - 20px)' : 870}
        heightContainer={isMobile ? 'calc(100% - 20px)' : 'auto'}
        maxHeightContainer="calc(100% - 20px)"
        title="Ajustar Total - Pessoal"
        headerIcon={<PlusCircleIcon />}
        onClose={handleClose}
        {...rest}
      >
        <S.Title>
          {canEdit
            ? 'Por favor, preencha todos os campos obrigatórios para adicionar um ajuste.'
            : 'Novos ajustes não estão disponíveis para esta prévia. Consulte abaixo o histórico de ajustes.'}
        </S.Title>

        <FormProvider {...methods}>
          <S.Form>
            <RowBox columns={2}>
              <Select
                required
                name="launch"
                label="Lançamento"
                placeholder=""
                options={LAUNCH_OPTIONS}
                disabled
              />

              <RowBox columns={2}>
                <DatePicker
                  required
                  name="date"
                  label="Data"
                  placeholder="DD/MM/AAAA"
                  minDate={filiData?.startPeriod ? parseISO(filiData.startPeriod) : undefined}
                  maxDate={filiData?.endPeriod ? parseISO(filiData.endPeriod) : undefined}
                  disabled={!canEdit || isLoading}
                  popperProps={{ strategy: 'fixed' }}
                />
                <Input
                  required
                  name="totalValue"
                  label="Valor total"
                  placeholder="R$ 0,00"
                  maskType="currency"
                  onBlur={({ target }) => methods.setValue('totalValue', target.value)}
                  disabled={!canEdit || isLoading}
                />
              </RowBox>
            </RowBox>

            <RowBox>
              <Textarea
                required
                name="observations"
                label="Observações"
                placeholder="Descreva as suas observações..."
                disabled={!canEdit || isLoading}
                style={{ minHeight: '116px' }}
              />
            </RowBox>

            {canEdit && (
              <S.Actions>
                <Button
                  colorType="outlineBlue"
                  width={isMobile ? 'calc(50% - 8px)' : 120}
                  disabled={isLoading}
                  onClick={handleClose}
                >
                  Cancelar
                </Button>
                <Button
                  colorType="solidBlue"
                  width={isMobile ? 'calc(50% - 8px)' : 210}
                  loading={isLoading}
                  onClick={() => methods.handleSubmit(onSubmit, onError)()}
                >
                  Confirmar Novo Ajuste
                </Button>
              </S.Actions>
            )}
          </S.Form>
        </FormProvider>

        {open && <AdjustHistory refreshKey={historyRefreshKey} />}
      </BaseModal2>

      <ReturnModal
        open={openCancelModal}
        headerIcon={<WarningIcon />}
        title="Deseja realmente cancelar a ação?"
        message="Todo o seu progresso será perdido!"
        headerTitle="Você tem certeza?"
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        onClose={setOpenCancelModal}
        onConfirm={() => {
          onClose(false)
          setOpenCancelModal(false)
        }}
      />
    </>
  )
}
